import { Checkbox, Tooltip } from 'antd';
import classNames from 'classnames';
import Lottie from 'react-lottie';
import { useEffect, useMemo, useState } from 'react';
import GeneratingSummary from 'src/modules/ambient_solo/components/PastEncounter/GeneratingSummary.json';
import { ScreenLoader } from 'src/components';
import {
  useGetEhrAppointments,
  useGeneratePreChartSummary,
} from 'src/hooks/ehr';
import {
  useGetEncounterDetails,
  useUpdateEncounterDetails,
  useLegacyEncounterStore,
} from 'src/hooks/encounter';
import { parseSummary } from 'src/utils';
import { PreChartDocuments } from './preChartDocuments';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import {
  Button,
  Card,
  Status,
  toast,
} from '@insight-health-ai/roseate/components';
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Microphone01,
  RefreshCcw05,
} from '@insight-health-ai/roseate/icons';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { RecordTranscription } from './recordTranscription';
import dayjs from 'dayjs';
import { useTheme } from '@insight-health-ai/roseate/theme';
import {
  useCreateSummaryVersion,
  useGetSummaryList,
  useUpdateSummary,
} from 'src/hooks/summary';
import { lightenColor } from '@insight-health-ai/roseate/utils';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';

export type EncounterPreChartProps = {
  encounterId: string;
  source: 'vca_patient_history' | 'pre_chart_history';
  selectedMicrophone: string; // todo: refactor this to in independent store
  legacySearchFilters: unknown;
};

const sectionsOrder = [
  'patient profile',
  'last visit',
  'treatment history',
  'data',
  'background',
  'diagnosis',
];

export const EncounterPreChart = ({
  encounterId,
  source,
  selectedMicrophone,
  // @todo: Harshit | remove this post migration
  legacySearchFilters,
}: EncounterPreChartProps) => {
  const { user } = useAuth();
  const theme = useTheme();
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [recording, setRecording] = useState<{
    active: boolean;
    since: number | null;
  }>({
    active: false,
    since: null,
  });
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const encounterChangedLocalStorageKey = `encounter:${encounterId}:preChart:lastChanged`;
  const [changeDetected, setChangeDetected] = useState(false);
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [hasTranscript, setHasTranscript] = useState<boolean>(false);

  const { data: currentPractitioner } = useGetCurrentPractitioner();
  const { data: encounterDetails, isLoading: isLoadingEncounterDetails } =
    useGetEncounterDetails(
      {
        encounterId,
      },
      {
        refetchInterval: (query) =>
          query.state.data?.pre_chart_status === 'summary_inprogress'
            ? 3000
            : false,
      }
    );
  const {
    mutateAsync: updateEncounterDetails,
    isPending: isUpdatingEncounterDetails,
  } = useUpdateEncounterDetails();
  const { updateEncounterList } = useLegacyEncounterStore();

  const {
    data: summaryList,
    isLoading: isLoadingSummaryList,
    refetch: refetchSummaryList,
  } = useGetSummaryList({
    encounterId,
    encounterPhase: 'pre-chart',
    type: 'ambient',
  });
  const {
    mutateAsync: createSummaryVersion,
    isPending: isCreatingSummaryVersion,
  } = useCreateSummaryVersion();

  const { mutateAsync: updateSummary, isPending: isUpdatingSummary } =
    useUpdateSummary();

  const busyUpdating =
    isUpdatingSummary || isCreatingSummaryVersion || isUpdatingEncounterDetails;

  const { data: appointmentsData, isLoading: isLoadingAppointments } =
    useGetEhrAppointments(
      {
        patientId: encounterDetails?.patient_id ?? 0,
        count: 1,
        practitionerId: encounterDetails?.practitioner_id ?? 0,
        appointmentId: encounterDetails?.appointment_id ?? 0,
      },
      {
        enabled: !!(
          encounterDetails &&
          encounterDetails.patient_id &&
          encounterDetails.appointment_id &&
          encounterDetails?.encounter_ehr_data?.appointment_ehr_id &&
          currentPractitioner?.org_settings?.ehr_precharting_enabled
        ),
      }
    );

  const { mutateAsync: generatePreChartSummary } = useGeneratePreChartSummary();

  const summary = summaryList?.summaries?.[0];
  const clinicalNote = appointmentsData?.clinical_notes?.[0];
  const appointmentStartTime = appointmentsData?.appointments?.[0]?.start_date;

  const { sectionsToRender, summaryJson, notesJson, isLegacySummary } =
    useMemo(() => {
      let summaryJson = {} as Record<string, string>;
      const isLegacySummary =
        !!summary?.summary_json &&
        !(summary?.summary_json || '').trim().startsWith('{');

      if (summary?.summary_json && !isLegacySummary) {
        try {
          summaryJson = JSON.parse(summary?.summary_json || '{}');
        } catch (error) {
          // ignore
        }
      }

      let notesJson = {} as Record<string, string>;
      if (clinicalNote?.content) {
        try {
          notesJson = JSON.parse(clinicalNote?.content || '{}');
        } catch (error) {
          // ignore
        }
      }

      return {
        summaryJson,
        isLegacySummary,
        notesJson,
        sectionsToRender: Object.entries({ ...notesJson, ...summaryJson })
          .map(([key, value]) => ({
            title: key,
            content: value as string,
            key,
            selected: !!summaryJson[key],
          }))
          .sort((a, b) => {
            const aIndex = sectionsOrder.indexOf(a.title.toLowerCase());
            const bIndex = sectionsOrder.indexOf(b.title.toLowerCase());
            // If both are not found, keep their original order
            if (aIndex === -1 && bIndex === -1) return 0;
            // If only a is not found, it comes after b
            if (aIndex === -1) return 1;
            // If only b is not found, it comes after a
            if (bIndex === -1) return -1;
            // Otherwise, sort by their index
            return aIndex - bIndex;
          }),
      };
    }, [clinicalNote?.content, summary?.summary_json]);

  useEffect(() => {
    if (encounterDetails?.pre_chart_status === 'completed') {
      refetchSummaryList();
    }
  }, [encounterDetails?.pre_chart_status === 'summary_inprogress']);

  const hasHistory = sectionsToRender.length > 0 || isLegacySummary;
  const allowSectionSelection = !!(
    !isLegacySummary && Object.keys(notesJson).length
  );

  const toggleSummarySections = async (sections: Record<string, boolean>) => {
    const newSections = {
      ...Object.keys(summaryJson).reduce(
        (acc, key) => {
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>
      ),
      ...sections,
    };

    const newSummaryJson = Object.fromEntries(
      sectionsToRender
        .filter(({ key }) => newSections[key])
        .map(({ key, content }) => [key, content])
    );

    try {
      if (summary) {
        await updateSummary({
          summaryId: summaryList?.summaries?.[0]?.summary_id!,
          payload: {
            summary_json: JSON.stringify(newSummaryJson),
          },
        });
      } else {
        await createSummaryVersion({
          encounterId,
          encounterPhase: 'pre-chart',
          version: 1,
          summaryJson: JSON.stringify(newSummaryJson),
        });
        await updateEncounterDetails({
          encounterId,
          encounterPhase: 'pre-chart',
          payload: {
            pre_chart_status: 'inprogress',
          },
        });
        /**
         * @todo: Harshit | remove this post migration
         */
        try {
          updateEncounterList(legacySearchFilters, {
            currentEncounterId: encounterId,
          });
        } catch (error) {
          // do nothing
        }
      }
    } catch {
      toast.error('Failed to update section(s)');
    }
  };

  const generateNotes = async () => {
    analytics.track(
      `Clicked Generate Pre-charting In Ambient From ${source === 'vca_patient_history' ? 'VCA' : 'Aura'}`,
      {
        email: user?.email,
      }
    );

    try {
      await generatePreChartSummary({
        encounterId,
        clinicalNoteId: clinicalNote?.note_id,
      });

      setChangeDetected(false);
      toast.success('Generated note will be available shortly.');
    } catch {
      toast.error('Failed to generate note');
    }
  };

  // const markChangeDetected = useCallback((value: boolean) => {
  //   if (value) {
  //     if (!changeDetected) {
  //       // store the last time a change was detected
  //       localStorage.setItem(
  //         encounterChangedLocalStorageKey,
  //         Date.now().toString()
  //       );
  //     }
  //   } else {
  //     localStorage.removeItem(encounterChangedLocalStorageKey);
  //   }
  //   setChangeDetected(value);
  // }, []);

  // useEffect(() => {
  //   const contextChanged = localStorage.getItem(
  //     encounterChangedLocalStorageKey
  //   );
  //   if (summary?.updated_at && contextChanged) {
  //     if (new Date(summary.updated_at).getTime() < Number(contextChanged)) {
  //       setChangeDetected(true);
  //     } else {
  //       setChangeDetected(false);
  //     }
  //   }
  // }, [!!summary?.updated_at]);

  if (
    isLoadingEncounterDetails ||
    isLoadingAppointments ||
    isLoadingSummaryList
  ) {
    return <ScreenLoader />;
  }

  if (encounterDetails?.pre_chart_status === 'summary_inprogress') {
    return (
      <div className="flex justify-center items-center min-h-[400px] w-full">
        <Lottie
          options={{
            loop: true,
            animationData: GeneratingSummary,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
            },
          }}
          style={{ maxWidth: '80%', maxHeight: '80%' }}
        />
      </div>
    );
  }

  const renderAudioControls = () => {
    if (recording.active) {
      return (
        <div className="flex items-center gap-2">
          <Status size="xs" label="◦ Recording" appearance="error" />
        </div>
      );
    }

    if (transcriptExpanded || !hasHistory) {
      return null;
    }
    return (
      <Button
        variant="outlined"
        appearance="primary"
        icon={<Microphone01 />}
        onClick={() => setTranscriptExpanded(true)}
      >
        Record pre-chart
      </Button>
    );
  };

  const renderRegenerateButton = () => {
    if (!hasHistory && !hasDocuments && !hasTranscript) {
      return null;
    }

    return (
      <Tooltip
        placement="topRight"
        styles={{
          body: {
            backgroundColor: lightenColor(theme.colors.warning, 90),
          },
        }}
        open={
          hasHistory &&
          changeDetected &&
          !recording?.active &&
          !isDocumentsModalOpen
        }
        title={
          <div className="flex gap-2 items-center">
            <ExclamationCircleFilled
              style={{
                color: theme.colors.warning,
                fontSize: theme.fontSize.lg[0],
              }}
            />
            <div className="flex flex-col">
              <div className="font-medium">Detected Changes</div>
              <div className="text-xs">Regenerate to view updated summary.</div>
            </div>
          </div>
        }
      >
        <Button
          appearance="primary"
          icon={hasHistory ? <RefreshCcw05 /> : <Check />}
          onClick={generateNotes}
        >
          {hasHistory ? 'Regenerate' : 'Generate'}
        </Button>
      </Tooltip>
    );
  };

  return (
    <div className="h-full w-full">
      <div className="flex flex-col gap-5 max-w-2xl mx-auto">
        <div className="flex flex-col gap-2 w-full sticky top-0 z-10 bg-white">
          <Card
            className="shadow"
            padding={{ header: 'sm', body: 'none' }}
            title={
              <div
                className="flex items-center justify-between w-full"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setTranscriptExpanded(!transcriptExpanded);
                  }
                }}
              >
                {hasHistory && (
                  <Button
                    variant="text"
                    appearance="secondary"
                    icon={transcriptExpanded ? <ChevronUp /> : <ChevronDown />}
                    onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                  />
                )}
                <div className="flex gap-2 right-0 ml-auto">
                  {renderAudioControls()}
                  <div>
                    <PreChartDocuments
                      encounterId={encounterId}
                      source={source}
                      onDocumentsChange={() => {
                        setChangeDetected(true);
                      }}
                      onChange={({ documents, notes }) => {
                        setHasDocuments(
                          documents.length > 0 || notes.content.length > 0
                        );
                      }}
                      onOpenChange={(isOpen) => {
                        setIsDocumentsModalOpen(isOpen);
                      }}
                    />
                  </div>
                  {renderRegenerateButton()}
                </div>
              </div>
            }
          >
            <RecordTranscription
              source={source}
              isExpanded={!hasHistory || transcriptExpanded}
              status={encounterDetails?.pre_chart_status ?? 'new'}
              encounterId={encounterId}
              encounterPhase="pre-chart"
              selectedMicrophone={selectedMicrophone}
              onRecordingChange={setRecording}
              onTranscriptionChange={() => {
                setChangeDetected(true);
              }}
              onChange={(transcription) => {
                setHasTranscript(transcription.length > 0);
              }}
              legacySearchFilters={legacySearchFilters}
            />
          </Card>
        </div>

        {isLegacySummary && (
          <div
            className="text-sm"
            dangerouslySetInnerHTML={{
              __html: parseSummary(summary?.summary_json || ''),
            }}
          />
        )}

        {sectionsToRender.length > 0 && (
          <div className="flex flex-col gap-2 pb-4 px-2">
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <div className="font-medium">Chart summary</div>
                {appointmentStartTime && (
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar />
                    Last appointment:{' '}
                    {dayjs(appointmentStartTime).format('MMM D, YYYY h:mm A')}
                  </span>
                )}
              </div>
              {allowSectionSelection && (
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-2 font-medium">
                    <Checkbox
                      checked={Object.values(sectionsToRender).every(
                        ({ selected }) => selected
                      )}
                      disabled={busyUpdating}
                      onChange={(e) => {
                        toggleSummarySections(
                          sectionsToRender.reduce(
                            (acc, section) => {
                              acc[section.key] = e.target.checked;
                              return acc;
                            },
                            {} as Record<string, boolean>
                          )
                        );
                      }}
                    />
                    Include all sections to In visit{' '}
                  </div>
                  <div className="text-sm text-gray-500 text-right">
                    {sectionsToRender.filter(({ selected }) => selected).length}{' '}
                    of {sectionsToRender.length} included
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4">
              {sectionsToRender.map((section) => (
                <div
                  key={section.title}
                  className={classNames(
                    'flex flex-col rounded-lg border border-primary',
                    {
                      'bg-green-100/30': section.selected,
                    }
                  )}
                >
                  <div
                    className={classNames(
                      'flex gap-2 items-center border-b p-4 shadow-sm',
                      {
                        'cursor-not-allowed':
                          allowSectionSelection && busyUpdating,
                        'cursor-pointer':
                          allowSectionSelection && !busyUpdating,
                      }
                    )}
                    onClick={() => {
                      if (busyUpdating || !allowSectionSelection) return;

                      toggleSummarySections({
                        [section.key]: !section.selected,
                      });
                    }}
                  >
                    {allowSectionSelection && (
                      <Checkbox
                        checked={section.selected}
                        disabled={busyUpdating}
                      />
                    )}
                    <div className="font-medium">{section.title}</div>
                    <div className="ml-auto right-0">
                      {section.selected ? (
                        <Status
                          size="xs"
                          label="Included"
                          style={{
                            backgroundColor: theme.colors.success,
                            color: theme.colors.white,
                          }}
                        />
                      ) : (
                        <Tooltip
                          title={
                            !allowSectionSelection ? (
                              <div className="flex flex-wrap overflow-wrap">
                                Press Regenerate to add to the summary.
                              </div>
                            ) : null
                          }
                          placement="topRight"
                        >
                          <Status
                            size="xs"
                            label="Not Included"
                            appearance="warning"
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 px-4 py-2">
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{
                        __html: parseSummary(section.content),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
